import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Clock, CheckCircle, Package, RefreshCw } from 'lucide-react';
import { PERMISSIONS } from '@pms/shared';
import { requirementsService, type RequirementListItem } from '@/services/requirements.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { StatCard } from '@/components/ui/stat-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/dateFormat';

const PRIORITY_BADGE: Record<string, string> = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-teal-100 text-teal-700 border-teal-200',
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const PRIORITY_BAR: Record<string, string> = {
  critical: 'bg-rose-500',
  high: 'bg-orange-500',
  medium: 'bg-teal-500',
  low: 'bg-emerald-500',
};

function OverviewSkeleton() {
  return (
    <div className="space-y-5">
      <div className="h-8 w-56 rounded-lg bg-muted/60 animate-pulse" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-card p-5 shadow-card animate-pulse">
            <div className="h-4 w-24 rounded bg-muted/60 mb-3" />
            <div className="h-8 w-16 rounded bg-muted/60" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function RequirementsOverviewPage() {
  const statsQuery = useQuery({
    queryKey: ['requirements', 'stats'],
    queryFn: () => requirementsService.getStats(),
  });

  if (statsQuery.isLoading) return <OverviewSkeleton />;

  if (statsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
        <p className="text-sm text-destructive">Failed to load overview.</p>
        <button
          onClick={() => statsQuery.refetch()}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  const stats = statsQuery.data;

  const approvedCount = stats?.by_status?.find(
    (s) => s.status_code?.toUpperCase() === 'APPROVED',
  )?.count ?? 0;

  const fulfilledCount = stats?.by_status?.find(
    (s) => s.status_code?.toUpperCase() === 'FULFILLED',
  )?.count ?? 0;

  const maxStatusCount = Math.max(1, ...(stats?.by_status ?? []).map((s) => s.count));
  const maxPriorityCount = Math.max(1, ...(stats?.by_priority ?? []).map((p) => p.count));

  const PRIORITY_ORDER = ['critical', 'high', 'medium', 'low'];
  const orderedPriorities = PRIORITY_ORDER.map((p) => ({
    priority: p,
    count: stats?.by_priority?.find((x) => x.priority === p)?.count ?? 0,
  }));

  const recentColumns: Column<RequirementListItem>[] = [
    {
      key: 'title',
      header: 'Title',
      cell: (r) => <span className="text-sm font-medium truncate max-w-xs block">{r.title}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      cell: (r) => r.category?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (r) => (
        <Badge variant="outline" className={`capitalize text-xs ${PRIORITY_BADGE[r.priority] ?? ''}`}>
          {r.priority}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) =>
        r.status ? (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.status.color }} />
            {r.status.name}
          </span>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      key: 'requested_by',
      header: 'Requested By',
      cell: (r) => r.requested_by?.name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'date',
      header: 'Date',
      cell: (r) => <span className="text-muted-foreground text-sm">{formatDate(r.created_at)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Requirements Overview</h1>

      <PermissionGate permission={PERMISSIONS.REQUIREMENTS_OVERVIEW}>
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <StatCard
              label="Total Requirements"
              value={stats?.total ?? '—'}
              tone="sky"
              icon={ClipboardList}
            />
            <StatCard
              label="Pending Approval"
              value={stats?.pending_approval ?? '—'}
              tone="amber"
              icon={Clock}
            />
            <StatCard
              label="Approved"
              value={approvedCount}
              tone="emerald"
              icon={CheckCircle}
            />
            <StatCard
              label="Fulfilled"
              value={fulfilledCount}
              tone="violet"
              icon={Package}
            />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* By Status */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>By Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(stats?.by_status ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No data</p>
                ) : (
                  (stats?.by_status ?? []).map((item) => (
                    <div key={item.status_id} className="space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color ?? '#94a3b8' }} />
                          <span className="text-sm truncate">{item.status_name}</span>
                        </div>
                        <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground shrink-0">
                          {item.count}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.round((item.count / maxStatusCount) * 100)}%`,
                            backgroundColor: item.color ?? '#94a3b8',
                            opacity: 0.6,
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* By Priority */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>By Priority</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {orderedPriorities.map((item) => (
                  <div key={item.priority} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm capitalize">{item.priority}</span>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground shrink-0 ml-2">
                        {item.count}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${PRIORITY_BAR[item.priority] ?? 'bg-primary/60'}`}
                        style={{ width: `${Math.round((item.count / maxPriorityCount) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Recent requirements */}
          <div className="mt-4">
            <h2 className="text-base font-semibold text-foreground mb-3">Recent Requirements</h2>
            <DataTable
              columns={recentColumns}
              rows={stats?.recent ?? []}
              rowKey={(r) => r.id}
              loading={statsQuery.isLoading}
              error={statsQuery.isError}
              onRetry={() => statsQuery.refetch()}
              empty="No requirements found."
            />
          </div>
        </>
      </PermissionGate>
    </div>
  );
}
